import { useEffect, useMemo, useState } from "react";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { createFilterOptions, FilterOptionsState } from "@mui/material";
import { toast } from "react-toastify";
import type { Branch, Contact, Deal, Grower, Job } from "../../../../../../models";
import { getDealLabel } from "../../../../../../utilities/utils";
import { CREATE_NEW_GROWER, CREATE_NEW_SALESPERSON } from "../../../../../../utilities/constants";
import { validateJobs, ValidationContext } from "../../../../../../utilities/jobValidation";
import { findDeal } from "../../ImportTable.helpers";
import { useDropdownOptions } from "../../../../shared/useDropdownOptions";
import type { JobDetailModalProps, JobDetailModalViewModel, ModalColumnContext } from "./JobDetailModal.types";

/**
 * ViewModel hook for JobDetailModal
 *
 * This hook manages:
 * - React Hook Form state and validation
 * - Deal configuration updates
 * - Grower/Salesperson autocomplete state
 * - Dual validation (declarative + programmatic)
 */
export function useJobDetailModalViewModel(props: JobDetailModalProps): JobDetailModalViewModel {
	const { job, company, allDeals, onSave, onClose, user, integration } = props;

	// ============= LOCAL STATE =============
	const [loading, setLoading] = useState(false);
	const [growerSelected, setGrowerSelected] = useState<Grower | { id: string; name: string } | null>(null);
	const [salespersonSelected, setSalespersonSelected] = useState<Contact | { id: string; name: string } | null>(null);
	const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
	const [microsRaw, setMicrosRaw] = useState(job?.microsList ?? "");

	// State for mobile column compatibility
	const [newSalespersons, setNewSalespersons] = useState<Contact[]>([]);
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
	const [readySelectionState, setReadySelectionState] = useState<Record<string, boolean>>({});

	// ============= DROPDOWN OPTIONS (shared hook) =============
	const { gridSizes, depths, labPackages, micros } = useDropdownOptions({ allDeals, job });

	// Find the current deal from job.dealIds
	const currentDeal = useMemo(() => {
		const dealId = job?.dealIds?.[0];
		return allDeals.find((d) => d.id === dealId);
	}, [job?.dealIds, allDeals]);

	const [deal, setDeal] = useState<Deal | { id: "custom"; label: "Create New Configuration" } | undefined>(currentDeal);

	// ============= REACT HOOK FORM =============
	const {
		register,
		handleSubmit,
		watch,
		control,
		setValue,
		trigger,
		setError,
		reset,
		formState: { errors, isValid, isDirty },
	} = useForm<Job>({
		mode: "onBlur",
		reValidateMode: "onBlur",
		defaultValues: job ?? undefined,
	});

	// Reset form when job changes (modal re-opens with different job)
	useEffect(() => {
		if (job) {
			// Reset form values to the new job
			reset(job);

			// Find and set initial deal
			const dealId = job.dealIds?.[0];
			const foundDeal = allDeals.find((d) => d.id === dealId);
			setDeal(foundDeal ?? { id: "custom", label: "Create New Configuration" });

			// Set initial grower selection
			if (job.growerIds?.[0] === CREATE_NEW_GROWER) {
				setGrowerSelected({ id: CREATE_NEW_GROWER, name: job.importGrowerName ?? "" });
			} else if (job.growerIds?.[0]) {
				// Find existing grower from the growers list
				const foundGrower = props.growers?.find((g) => g.id === job.growerIds?.[0]);
				if (foundGrower) {
					setGrowerSelected(foundGrower);
				} else {
					// Grower ID exists but not in the list - use importGrowerName as fallback
					setGrowerSelected(job.importGrowerName ? { id: job.growerIds[0], name: job.importGrowerName } : null);
				}
			} else if (job.importGrowerName) {
				// No grower ID but has a name - treat as creating new grower
				setGrowerSelected({ id: CREATE_NEW_GROWER, name: job.importGrowerName });
			} else {
				setGrowerSelected(null);
			}

			// Set initial salesperson selection
			if (job.salespersonIds?.[0] === CREATE_NEW_SALESPERSON) {
				setSalespersonSelected({ id: CREATE_NEW_SALESPERSON, name: job.newSalesperson?.name ?? "" });
			} else if (job.salespersonIds?.[0]) {
				// Find existing salesperson from the salespersons list
				const foundSalesperson = props.salespersons?.find((s) => s.id === job.salespersonIds?.[0]);
				if (foundSalesperson) {
					setSalespersonSelected(foundSalesperson);
				} else {
					setSalespersonSelected(null);
				}
			} else {
				setSalespersonSelected(null);
			}

			// Set initial branch
			const branchId = job.branchIds?.[0];
			const foundBranch = props.branches?.find((b) => b.id === branchId);
			setSelectedBranch(foundBranch ?? null);

			// Set initial microsRaw
			setMicrosRaw(job.microsList ?? "");
		}
	}, [job, allDeals, props.branches, props.growers, props.salespersons, reset]);

	// ============= VALIDATION CONTEXT =============
	// Watch form fields needed for validation context
	const pointsFiles = watch("files.pointsSHP");
	const salespersonIds = watch("salespersonIds");

	const validationContext: ValidationContext = useMemo(() => {
		return {
			company: company ?? undefined,
			deal: deal?.id === "custom" ? undefined : (deal as Deal | undefined),
			allDeals,
			job: job ?? undefined,
			user: user ?? undefined,
			hasPointsFile: !!(pointsFiles && (pointsFiles as File[]).length > 0),
			isCreatingNewSalesperson: salespersonIds?.[0] === CREATE_NEW_SALESPERSON,
			integration,
		};
	}, [company, deal, allDeals, job, user, pointsFiles, salespersonIds, integration]);

	// ============= SALESPERSON HELPERS =============
	const isNewSalesperson = (id: string): boolean => id.startsWith("temp-");

	const findNewSalesperson = (id: string): Contact | undefined => {
		return newSalespersons.find((sp) => sp.id === id);
	};

	// ============= COLUMN CONTEXT =============
	// This provides compatibility with mobile column components
	const currentFormValues = watch();

	const columnContext: ModalColumnContext = useMemo(() => {
		// Create a setJobs function that updates the form values
		const setJobs = (updater: Job[] | ((prev: Job[]) => Job[])) => {
			const jobs = [currentFormValues as Job];
			const updated = typeof updater === "function" ? updater(jobs) : updater;
			const updatedJob = updated[0];

			if (updatedJob) {
				// Update each changed field via react-hook-form
				Object.keys(updatedJob).forEach((key) => {
					const typedKey = key as keyof Job;
					const newValue = updatedJob[typedKey];
					const oldValue = currentFormValues[typedKey];
					if (newValue !== oldValue) {
						setValue(typedKey, newValue as any);
					}
				});
			}
		};

		// Create a validate function that updates validationErrors state
		const validateJobsFn = (jobsToValidate: Job[]) => {
			const errors = validateJobs(jobsToValidate, {
				...validationContext,
				job: jobsToValidate[0],
			});
			setValidationErrors(errors);
			return errors;
		};

		return {
			props: {
				allDeals,
				selectedCompany: company,
				salespersons: props.salespersons,
				branches: props.branches,
				growers: props.growers,
				lastCropOptions: props.lastCropOptions,
				nextCropOptions: props.nextCropOptions,
			},
			// Clone the job to prevent mutation issues - setJobs compares against currentFormValues
			jobs: [{ ...currentFormValues } as Job],
			setJobs,
			validateJobs: validateJobsFn,
			validationContext,
			newSalespersons,
			setNewSalespersons,
			isNewSalesperson,
			findNewSalesperson,
			readySelectionState,
			setReadySelectionState,
			gridSizes,
			depths,
			labPackages,
			micros,
			findDeal,
		};
	}, [
		currentFormValues,
		setValue,
		validationContext,
		allDeals,
		company,
		props.salespersons,
		props.branches,
		props.growers,
		props.lastCropOptions,
		props.nextCropOptions,
		newSalespersons,
		readySelectionState,
		gridSizes,
		depths,
		labPackages,
		micros,
	]);

	// ============= DEAL UPDATE LOGIC =============
	const frequencyOfMicros = watch("externalFrequencyOfMicros");
	const density = watch("importDensity");
	const depth = watch("importDepth");
	const testPackage = watch("externalTestPackageId");
	const dealIds = watch("dealIds");

	const updateDeal = () => {
		const currentDealId = dealIds?.[0];
		const foundDeal = allDeals.find((d) => d.id === currentDealId);

		if (foundDeal) {
			if (deal?.id !== foundDeal.id) {
				setDeal(foundDeal);
				// Update form values from deal configuration
				setValue("importDensity", foundDeal.density);
				setValue("importDepth", foundDeal.depth);
				setValue("externalTestPackageId", foundDeal.testPackage);
				setValue("externalFrequencyOfMicros", foundDeal.frequencyOfMicros);
			}
		} else {
			if (deal?.id !== "custom") {
				setDeal({ id: "custom", label: "Create New Configuration" });
			}
		}
	};

	useEffect(() => {
		updateDeal();
		// Note: `deal` intentionally excluded from deps to prevent infinite loop
	}, [frequencyOfMicros, density, depth, testPackage, dealIds]);

	// ============= HELPER FUNCTIONS =============
	const getDealLabelForDropdown = (option: Deal | { id: string; label: string }): string => {
		if (option && option.id === "custom") {
			return "Create New Configuration";
		}
		return getDealLabel(option as Deal);
	};

	const parseMicrosList = (rawValue: string): string => {
		// Parse comma-separated list to unique alphanumeric values
		return rawValue
			.split(",")
			.map((s) => s.trim())
			.filter((s) => /^[a-zA-Z0-9]+$/.test(s))
			.filter((s, i, arr) => arr.indexOf(s) === i)
			.join(",");
	};

	// ============= SUBMIT HANDLERS =============
	const onSubmit: SubmitHandler<Job> = async (data) => {
		setLoading(true);

		try {
			// Step 1: Run programmatic validation (business logic)
			const validationErrors = validateJobs([data], {
				...validationContext,
				job: data,
			});

			// Step 2: If errors exist, set them in the form and stop
			if (Object.keys(validationErrors).length > 0) {
				Object.entries(validationErrors).forEach(([key, message]) => {
					// Extract field name from key (format: "fieldName-jobId")
					const fieldName = key.split("-")[0];
					setError(fieldName as keyof Job, { message });
				});
				toast.error("Please fix validation errors before saving.");
				setLoading(false);
				return;
			}

			// Step 3: Process special values before saving
			// Handle grower creation
			if (data.growerIds?.[0] === CREATE_NEW_GROWER) {
				data.growerIds = [];
			}
			if (data.importGrowerName?.startsWith("New Grower: ")) {
				data.importGrowerName = data.importGrowerName.replace(/^New Grower: /, "");
			}

			// Handle salesperson creation
			if (data.salespersonIds?.includes(CREATE_NEW_SALESPERSON)) {
				// Keep the newSalesperson data but clear the sentinel
				data.salespersonIds = [];
			}

			// Handle custom deal
			if (data.dealIds?.[0] === "custom") {
				data.dealIds = [];
			}

			// Handle branch
			if (selectedBranch) {
				data.branchIds = [selectedBranch.id];
			}

			// Parse microsList
			if (microsRaw) {
				data.microsList = parseMicrosList(microsRaw);
			}

			// Step 4: Call onSave and close modal
			onSave(data);
			onClose();
			toast.success("Job updated successfully!");
		} catch (error: any) {
			console.error("Error saving job:", error);
			toast.error("Failed to save job. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const onError: SubmitErrorHandler<Job> = (errors) => {
		toast.error("Please fill in all required fields correctly.");

		// Show individual error messages
		Object.entries(errors).forEach(([, error]) => {
			if (error?.message) {
				toast.error(error.message);
			}
		});
	};

	// ============= CLOSE HANDLER =============
	const handleClose = () => {
		if (isDirty) {
			if (!window.confirm("You have unsaved changes. Are you sure you want to close?")) {
				return;
			}
		}
		onClose();
	};

	// ============= RETURN VIEWMODEL =============
	return {
		// React Hook Form
		register,
		handleSubmit,
		control,
		watch,
		setValue,
		trigger,
		errors,
		isValid,
		isDirty,

		// Validation
		validationContext,
		validationErrors,

		// Column context for mobile columns
		columnContext,

		// State
		loading,
		growerSelected,
		salespersonSelected,
		selectedBranch,
		microsRaw,
		deal,

		// State setters
		setGrowerSelected,
		setSalespersonSelected,
		setSelectedBranch,
		setMicrosRaw,

		// Handlers
		onSubmit,
		onError,
		handleClose,

		// Helper functions
		getDealLabelForDropdown,
		parseMicrosList,
		updateDeal,
		filter: <T>(options: T[], state: FilterOptionsState<T>) => createFilterOptions<T>()(options, state),
	};
}
